(ns app.front.view.admin.tenant-management.detail-view
  "System-admin tenant detail page: heading, breadcrumb and tabs. Each tab is its
  own namespace and covers the whole tenant."
  (:require [app.domain.group.model :as group-model]
            [app.front.ui :as ui]
            [app.front.view.admin.domain-groups.municipality-groups :as mg]
            [app.front.view.admin.tenant-management.features-tab :as features-tab]
            [app.front.view.admin.tenant-management.municipalities-tab :as municipalities-tab]
            [app.front.view.admin.tenant-management.overview-tab :as overview-tab]
            [app.front.view.admin.tenant-management.products-tab :as products-tab]
            [app.front.view.admin.tenant-management.subs :as tm-subs]
            [app.front.view.admin.tenant-management.user-subs :as user-subs]
            [app.front.view.admin.tenant-management.users-tab :as users-tab]
            [app.front.view.group.admin.common :as acommon]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [potpuri.core :as p]
            [schema.core :as s]))

;; ---------------------------------------------------------------------------
;; Small building blocks
;; ---------------------------------------------------------------------------

(defn- status-pill [tr active?]
  [ui/tag {:type  :round
           :color (if active? :blue :light-gray)}
   (tr [(if active? :committee.state/active :committee.state/inactive)])])

;; ---------------------------------------------------------------------------
;; Page
;; ---------------------------------------------------------------------------

(defn- region-text [tr {:keys [country subdivision]}]
  (->> [(when subdivision (tr [(keyword "subdivision" (name subdivision))]))
        (when country (tr [(keyword "country" (name country))]))]
       (remove nil?)
       (interpose ", ")
       (apply str)))

(defn- heading
  "Info-decorated page heading: name + status, then the tenant's identifying
  details. Replaces `admin-page-v2`'s plain `<h1>`."
  [tr {:keys [name code active? authority-count] :as tenant}]
  [:div.my-10
   [:div.flex.items-center.gap-3.flex-wrap
    [:h1.m-0.font-semibold name]
    [status-pill tr active?]]
   [:p.text-gray-600.mt-1.pb-0
    (->> [code
          (not-empty (region-text tr tenant))
          (tr [:admin/n-sub-groups] [authority-count])]
         (remove nil?)
         (interpose " · ")
         (apply str))]])

(defn- tabs
  "Tab content components take no arguments (see `admin-page-v2`), so each one is
  a closure over the current tenant."
  [tr tenant-id tenant]
  [[:admin/overview
    (fn [] [overview-tab/overview-tab tr tenant])]
   [:admin/products
    (fn [] [products-tab/products-tab tr tenant])
    ;; Category state lives in a separate request; fetch it when the tab opens.
    #(>evt [::products-tab/fetch-product-configs (-> tenant :group :group/id)])]
   [:admin/features
    (fn [] [features-tab/features-tab tr tenant])]
   [:admin/tenant-municipalities
    (fn [] [municipalities-tab/municipalities-tab tr tenant])]
   [:admin/users
    (fn [] [users-tab/users-tab tr tenant-id])]
   [:admin/tenant-service-accounts
    (fn [] [users-tab/service-accounts-tab
            tr tenant-id
            (group-model/use-api-keys? (:group tenant))])]])

(defn view [match]
  (let [tr        (<sub [:tr])
        tenant-id (-> match :parameters :path :tenant-id)
        tenant    (<sub [::tm-subs/tenant tenant-id])
        loading?  (<sub [::tm-subs/loading?])]
    [acommon/admin-page-v2
     (p/assoc-if {:match match
                  :title :view/admin.tenant-management}
                 ;; Without a tenant, the plain title and the loading /
                 ;; not-found state instead of tabs.
                 :heading (when tenant [heading tr tenant])
                 :tabs (when tenant (tabs tr tenant-id tenant)))
     (when-not tenant
       (if loading?
         [:div.py-8 (cc/inline-loading {:description (tr [:loading])})]
         [:div.py-8 (tr [:admin/tenant-not-found])]))]))

(defn tenant-breadcrumb
  "Last breadcrumb item: the tenant's own name. Dropped while the tenant is still
  loading, leaving the parent \"Tenant management\" crumb as the last one."
  [{:keys [match]}]
  (let [tenant-id (-> match :parameters :path :tenant-id)]
    (when-let [tenant-name (:name (<sub [::tm-subs/tenant tenant-id]))]
      [:view/admin.tenant-management.detail {:tenant-id tenant-id} tenant-name])))

(def route
  {:name        :view/admin.tenant-management.detail
   :view        view
   ;; admin-page-v2 needs to own the layout, or main-wrapper clips its branded
   ;; background at the page edges.
   :own-layout? true
   ;; See the create route: `/new` is a literal sibling of this `:tenant-id`.
   :conflicting true
   ;; Puts "Tenant management" between Dashboard and the tenant in the sub-header
   ;; breadcrumb, and points its Back button at the tenant list.
   :breadcrumb  {:items    [:view/admin.tenant-management]
                 :fallback tenant-breadcrumb}
   :view-sub    [::tm-subs/detail-view-data]
   :parameters  {:path {:tenant-id s/Int}}
   :controllers [{:parameters {:path [:tenant-id]}
                  :start      (fn [{{:keys [tenant-id]} :path}]
                                ;; Reopening a tenant starts out of edit mode.
                                (>evt [::overview-tab/cancel-edit])
                                (>evt [::tm-subs/fetch-tenant tenant-id])
                                (>evt [::mg/fetch-mobile-app-safe-group-ids])
                                (>evt [::user-subs/fetch-users tenant-id]))}]})
